import { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttForm, SttDialog } from '../../../sporttia/all';
import { PERMISSIONS } from '../../../lib/permissions';

type Role = {
    id?: number;
    name?: string;
    description?: string;
    privileges?: unknown;
};

type Props = {
    role: Role | null;
    onClose: (role: Role | null) => void;
};

export default function RoleDialog({
    role = null,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [internalRole, setInternalRole] = useState<Role>({});

    /**
     * Role changes
     */
    useEffect(() => {
        if (role) setInternalRole(role);
    }, [role]);

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setInternalRole({ ...internalRole, [name]: value });
    };

    /**
     * Save role
     */
    const saveRole = () => {
        // Updating
        if (internalRole.id) {
            cxt?.api('PUT', `/scs/roles/${internalRole.id}`, {
                params: {
                    name: internalRole.name,
                    description: internalRole.description,
                },
                success: (r: { role: Role }) => {
                    cxt?.showMessage('S', t('Saved'));
                    onClose({ privileges: role?.privileges, ...r.role });
                },
            });

            // Creating
        } else {
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/roles`, {
                params: {
                    name: internalRole.name,
                    description: internalRole.description,
                },
                success: (r: { role: Role }) => {
                    cxt?.showMessage('S', t('Created'));
                    onClose({ privileges: role?.privileges, ...r.role });
                },
            });
        }
    };

    /**
     * Delete role
     */
    const delRole = () => {
        cxt?.api('DELETE', `/scs/roles/${internalRole.id}`, {
            confirmation: true,
            success: (r: { role: Role }) => {
                cxt?.showMessage('S', t('Deleted'));
                onClose(r.role);
            },
        });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('Role')}
            open={role !== null}
            onClose={() => onClose(null)}
            content={
                <Box mt={3}>
                    <SttForm
                        elements={[
                            {
                                md: 12,
                                sm: 12,
                                xs: 12,
                                name: 'name',
                                value: internalRole.name,
                            },
                            {
                                md: 12,
                                sm: 12,
                                xs: 12,
                                name: 'description',
                                value: internalRole.description,
                                type: 'textarea',
                            },
                        ]}
                        onChangeForm={handleForm}
                    />
                </Box>
            }
            buttons={[
                {
                    show: (internalRole.id || 0) > 0,
                    type: 'delete',
                    onClick: delRole,
                    permission: PERMISSIONS.ADMINISTRATION.DELETE,
                },
                {
                    type: 'save',
                    onClick: saveRole,
                    permission:
                        (internalRole.id || 0) > 0
                            ? PERMISSIONS.ADMINISTRATION.MODIFY
                            : PERMISSIONS.ADMINISTRATION.CREATE,
                },
            ]}
        />
    );
}
